import type { Candidate } from "@webscan/decide.js";
import type { Identification } from "../protocol/events";
import type { CaptureVerdict } from "../shared/captures";
import type { CapturePriceRow } from "./capture-panel";
import { formatPrice, potentialPrice, type PotentialPrice } from "./prices";

export type FindCurrency = "USD" | "EUR";

export interface FindValueRange {
  currency: FindCurrency;
  low: number;
  high: number;
}

/** Values are cents; distinct currencies must never be added together. */
export interface FindSummary {
  count: number;
  unresolved: number;
  values: FindValueRange[];
  unpriced: number;
}

export interface FindPrice {
  labels: string[];
  notes: string[];
  minimum: number | null;
  values: FindValueRange[];
  unpriced: boolean;
}

export function printingLabel(printing: { setCode: string; collector: string }): string {
  return `${printing.setCode.toUpperCase()} #${printing.collector}`;
}

function finishNotes(price: PotentialPrice): string[] {
  if (!price.foil) return [];
  const notes = ["Foil potential"];
  if (price.plain !== null && price.plain !== price.cents) {
    notes.push(`${formatPrice(price.plain, price.currency)} non-foil`);
  }
  return notes;
}

function valueRanges(prices: PotentialPrice[]): FindValueRange[] {
  const ranges: FindValueRange[] = [];
  for (const currency of ["USD", "EUR"] as const) {
    const cents = prices
      .filter((price) => price.currency === currency && price.cents !== null)
      .map((price) => price.cents as number);
    if (cents.length) ranges.push({ currency, low: Math.min(...cents), high: Math.max(...cents) });
  }
  return ranges;
}

export function formatFindRange({ currency, low, high }: FindValueRange): string {
  if (low === high) return formatPrice(low, currency);
  const compact = (value: number): string => formatPrice(value, currency).replace(/\.00$/, "");
  return `${compact(low)}–${compact(high).replace(/^[$€]/, "")}`;
}

function range(prices: PotentialPrice[], currency: "USD" | "EUR"): string | null {
  const cents = prices
    .filter((price) => price.currency === currency && price.cents !== null)
    .map((price) => price.cents as number);
  if (!cents.length) return null;
  const low = Math.min(...cents);
  const high = Math.max(...cents);
  return formatFindRange({ currency, low, high });
}

/** Confirmed pre-resolution policy: candidate min–max potential, separated by currency. */
export function findPrice(
  identification: Pick<Identification, "printing"> & { verdict: CaptureVerdict | "REJECTED" },
  candidates: readonly Candidate[],
): FindPrice {
  const printing = identification.printing;
  if (!printing) return { labels: ["—"], notes: [], minimum: null, values: [], unpriced: true };
  const leader = potentialPrice(printing);
  if (identification.verdict !== "AMBIGUOUS" || candidates.length < 2) {
    return {
      labels: [formatPrice(leader.cents, leader.currency)],
      notes: finishNotes(leader),
      minimum: leader.cents,
      values: valueRanges([leader]),
      unpriced: leader.cents === null,
    };
  }

  const prices = candidates.map((candidate) => potentialPrice(candidate.printing));
  const allPriced = prices.every((price) => price.cents !== null);
  const labels = [range(prices, "USD"), range(prices, "EUR")].filter(
    (label): label is string => label !== null,
  );
  const notes: string[] = [];
  if (prices.some((price) => price.foil)) notes.push("Foil potential");
  if (labels.length && !allPriced) notes.push("Some unpriced");
  return {
    labels: labels.length ? labels : ["—"],
    notes,
    minimum: allPriced ? Math.min(...prices.map((price) => price.cents as number)) : null,
    values: valueRanges(prices),
    unpriced: !allPriced,
  };
}

/** Finish and printing details stay behind the capture, away from the scan surface. */
export function priceBreakdown(
  identification: Identification,
  candidates: Candidate[],
): CapturePriceRow[] {
  if (!identification.printing) return [];
  const printings =
    identification.verdict === "AMBIGUOUS" && candidates.length > 1
      ? candidates.map((candidate) => candidate.printing)
      : [identification.printing];
  return printings.flatMap((printing) => {
    const potential = potentialPrice(printing);
    const prefix = printings.length > 1 ? `${printingLabel(printing)} · ` : "";
    const plain = potential.currency === "USD" ? printing.usd : printing.eur;
    const foil = potential.currency === "USD" ? printing.usdFoil : printing.eurFoil;
    return [
      { label: `${prefix}Non-foil`, value: formatPrice(plain, potential.currency) },
      { label: `${prefix}Foil`, value: formatPrice(foil, potential.currency) },
    ];
  });
}
